import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogOut, Plus, Package, ShoppingBag, Wallet, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { inr, formatDate, statusColor } from "@/lib/format";

export default function SellerDashboard() {
  const { slug } = useParams();
  const { user, tenant, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/seller/dashboard").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const cards = stats ? [
    { l: "Products", v: stats.products, i: Package, c: "bg-blue-50 text-blue-700" },
    { l: "Orders", v: stats.orders, i: ShoppingBag, c: "bg-indigo-50 text-indigo-700" },
    { l: "Gross Revenue", v: inr(stats.gross_revenue), i: TrendingUp, c: "bg-emerald-50 text-emerald-700" },
    { l: "Pending Payout", v: inr(stats.pending_payout), i: Wallet, c: "bg-amber-50 text-amber-700" },
    { l: "Lifetime Paid", v: inr(stats.lifetime_payout), i: Wallet, c: "bg-violet-50 text-violet-700" },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`/tenant/${slug}/seller`} className="flex items-center gap-2" data-testid="seller-logo">
            <div className="w-8 h-8 rounded bg-slate-900 grid place-items-center text-white font-bold">S</div>
            <div>
              <div className="font-display font-bold leading-none">{user?.name}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Seller @ {tenant?.name}</div>
            </div>
          </Link>
          <Button variant="outline" size="sm" onClick={() => { logout(); nav(`/tenant/${slug}/seller/login`); }} data-testid="seller-logout-btn"><LogOut className="h-3.5 w-3.5 mr-2" /> Sign out</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="font-display text-3xl font-bold tracking-tight mb-1" data-testid="seller-dashboard-title">Dashboard</h1>
        <p className="text-slate-500 text-sm mb-6">Your marketplace performance.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          {cards.map((c) => {
            const Icon = c.i;
            return (
              <Card key={c.l} className="p-4 border-slate-200" data-testid={`seller-stat-${c.l.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className={`w-8 h-8 rounded grid place-items-center ${c.c}`}><Icon className="h-4 w-4" /></div>
                <div className="text-2xl font-display font-bold mt-2">{c.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">{c.l}</div>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue="products">
          <TabsList>
            <TabsTrigger value="products" data-testid="tab-seller-products">My Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-seller-orders">Orders</TabsTrigger>
            <TabsTrigger value="settlements" data-testid="tab-seller-settlements">Settlements</TabsTrigger>
          </TabsList>
          <TabsContent value="products"><ProductsTab /></TabsContent>
          <TabsContent value="orders"><OrdersTab /></TabsContent>
          <TabsContent value="settlements"><SettlementsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category_id: "", brand_id: "", images: [""], pricing: { daily: 0, monthly: 0 }, deposit: 0, stock: 1, is_active: true });

  const refresh = () => {
    api.get("/tenant/products").then(({ data }) => setProducts(data));
    api.get("/tenant/categories").then(({ data }) => setCats(data));
    api.get("/tenant/brands").then(({ data }) => setBrands(data));
  };
  useEffect(() => { refresh(); }, []);
  const save = async () => {
    try {
      await api.post("/tenant/products", form);
      toast.success("Product added");
      setOpen(false);
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="p-5 mt-4 border-slate-200">
      <div className="flex justify-end mb-4">
        <Button onClick={() => setOpen(true)} data-testid="seller-add-product-btn"><Plus className="h-4 w-4 mr-2" />Add Product</Button>
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {products.map((p) => (
          <Card key={p.id} className="p-3 border-slate-200" data-testid={`seller-product-${p.id}`}>
            {p.images?.[0] && <img src={p.images[0]} className="w-full h-32 object-cover rounded mb-2" alt="" />}
            <div className="font-medium text-sm">{p.name}</div>
            <div className="text-xs text-slate-500">{inr(p.pricing?.daily)}/day • {inr(p.pricing?.monthly)}/mo</div>
          </Card>
        ))}
        {products.length === 0 && <p className="text-sm text-slate-400 col-span-3">No products yet.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="seller-product-name" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Category</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="seller-product-cat"><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Brand</Label>
                <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Image URL</Label><Input value={form.images[0]} onChange={(e) => setForm({ ...form, images: [e.target.value] })} /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Daily ₹</Label><Input type="number" value={form.pricing.daily} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, daily: +e.target.value } })} data-testid="seller-product-daily" /></div>
              <div><Label>Monthly ₹</Label><Input type="number" value={form.pricing.monthly} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, monthly: +e.target.value } })} /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: +e.target.value })} /></div>
            </div>
            <Button onClick={save} className="w-full" data-testid="seller-product-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/seller/orders").then(({ data }) => setOrders(data)); }, []);
  return (
    <Card className="p-0 mt-4 border-slate-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="text-left px-3 py-2">Order</th><th className="text-left px-3 py-2">Items</th><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">My share</th></tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-t border-slate-100" data-testid={`seller-order-row-${o.id}`}>
              <td className="px-3 py-2 font-mono text-xs">#{o.id.slice(0, 8)}</td>
              <td className="px-3 py-2 text-xs">{o.items.length} items</td>
              <td className="px-3 py-2 text-xs">{formatDate(o.created_at)}</td>
              <td className="px-3 py-2"><span className={`pill ${statusColor(o.status)}`}>{o.status}</span></td>
              <td className="px-3 py-2 text-right">
                <div className="font-semibold">{inr(o.my_breakdown?.payout || 0)}</div>
                <div className="text-xs text-slate-500">−{inr(o.my_breakdown?.commission || 0)} commission</div>
              </td>
            </tr>
          ))}
          {orders.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-6">No orders</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function SettlementsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/seller/settlements").then(({ data }) => setItems(data)); }, []);
  return (
    <Card className="p-0 mt-4 border-slate-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="text-left px-3 py-2">Period</th><th className="text-left px-3 py-2">Gross</th><th className="text-left px-3 py-2">Commission</th><th className="text-left px-3 py-2">Payout</th><th className="text-left px-3 py-2">Status</th></tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-t border-slate-100">
              <td className="px-3 py-2 text-xs">{formatDate(s.period_from)} → {formatDate(s.period_to)}</td>
              <td className="px-3 py-2">{inr(s.gross_amount)}</td>
              <td className="px-3 py-2">{inr(s.commission_amount)}</td>
              <td className="px-3 py-2 font-semibold">{inr(s.net_payout)}</td>
              <td className="px-3 py-2"><span className={`pill ${statusColor(s.status)}`}>{s.status}</span></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-6">No settlements</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}
